import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Eye, Check, Users } from "lucide-react";
import { api } from "../lib/api";
import useEscClose from "../hooks/useEscClose";
import FormError from "./ui/FormError";
import { confirmDialog } from "../utils/dialogs";

const STATUS_CLS = { low: "text-amber-300", high: "text-rose-300", ok: "text-emerald-300" };

/**
 * Admin: review, preview and apply male / female reference bands.
 * Nothing is written until the admin clicks Apply on the selected markers.
 */
export default function GenderPresetsModal({ open, onClose, onApplied }) {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(null);

  useEscClose(open && !busy, onClose);

  useEffect(() => {
    if (!open) return;
    setError(""); setPreview(null); setDone(null); setLoading(true);
    api.get("/admin/param-ranges/gender-presets")
      .then((r) => {
        const list = (r.data?.presets || []).filter((p) => p.exists);
        setRows(list);
        setSelected(Object.fromEntries(list.map((p) => [p.key, true])));
      })
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Could not load presets"))
      .finally(() => setLoading(false));
  }, [open]);

  const setBand = (key, side, bound, value) => {
    setPreview(null);
    setRows((prev) => prev.map((p) => (p.key !== key ? p : {
      ...p, [side]: { ...p[side], [bound]: value === "" ? null : Number(value) },
    })));
  };

  const payload = () => ({
    presets: rows.filter((p) => selected[p.key])
      .map((p) => {
        const u = p.under15;
        const hasU15 = u && (u.low != null || u.high != null);
        return { key: p.key, male: p.male, female: p.female, ...(hasU15 ? { under15: u } : {}) };
      }),
  });

  const runPreview = async () => {
    setBusy("preview"); setError("");
    try {
      const r = await api.post("/admin/param-ranges/gender-presets/preview", payload());
      setPreview(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Preview failed");
    } finally { setBusy(""); }
  };

  const apply = async () => {
    const body = payload();
    const ok = await confirmDialog(
      `Apply male/female ranges for ${body.presets.length} marker(s)?\n\n` +
      `${preview?.total_flips ?? 0} athlete flag(s) will change. Every change is audit-logged and can be edited later in Reference ranges.`,
      { confirmLabel: "Apply" },
    );
    if (!ok) return;
    setBusy("apply"); setError("");
    try {
      const r = await api.post("/admin/param-ranges/gender-presets/apply", body);
      setDone(r.data);
      onApplied?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Apply failed");
    } finally { setBusy(""); }
  };

  if (!open) return null;
  const nSelected = Object.values(selected).filter(Boolean).length;

  return createPortal(
    <div className="fixed inset-0 z-[210] bg-[#040D1A]/90 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-6 px-3"
         data-testid="gender-presets-modal"
         onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
      <div className="card w-full max-w-4xl p-5 md:p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
              <Users size={12} /> Admin · Reference ranges
            </div>
            <div className="font-display text-lg text-slate-100">Gender &amp; age range presets</div>
            <div className="text-[11px] text-slate-400 mt-0.5 max-w-2xl leading-snug">
              Typical published ranges — confirm with your team doctor or your lab's printed ranges before applying.
              The Under-15 band (age on the lab date) takes priority over the boys/girls bands.
              Edit any value, preview which athletes' flags change, then apply.
            </div>
          </div>
          <button onClick={onClose} disabled={!!busy} className="pill !p-1.5" data-testid="gender-presets-close-btn">
            <X size={14} />
          </button>
        </div>

        {error && <div className="mb-3"><FormError message={error} onDismiss={() => setError("")} testId="gender-presets-error" /></div>}

        {done ? (
          <AppliedBanner done={done} onClose={onClose} />
        ) : loading ? (
          <div className="py-10 text-center text-slate-400"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</div>
        ) : (
          <>
            <PresetTable rows={rows} selected={selected} setSelected={(k, v) => { setPreview(null); setSelected((s) => ({ ...s, [k]: v })); }} setBand={setBand} />
            <div className="flex flex-wrap items-center justify-end gap-2 mt-4">
              <button onClick={runPreview} disabled={!!busy || nSelected === 0} className="pill disabled:opacity-40" data-testid="gender-presets-preview-btn">
                {busy === "preview" ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Preview impact
              </button>
              <button onClick={apply} disabled={!!busy || !preview || nSelected === 0} className="pill active disabled:opacity-40"
                      title={preview ? "" : "Preview the impact first"} data-testid="gender-presets-apply-btn">
                {busy === "apply" ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Apply {nSelected} marker{nSelected === 1 ? "" : "s"}
              </button>
            </div>
            {preview && <ImpactPreview preview={preview} rows={rows} />}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

function fmtBand(b) {
  if (!b) return "—";
  return `${b.low ?? "…"} – ${b.high ?? "…"}`;
}

function BandInput({ value, onChange, testid }) {
  return (
    <input type="number" step="any" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
           className="no-spinner w-16 px-1.5 py-1 bg-[#0E1A2E] border border-[#1E304B] rounded text-[12px] text-slate-100"
           data-testid={testid} />
  );
}

function PresetTable({ rows, selected, setSelected, setBand }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]" data-testid="gender-presets-table">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-slate-500 text-left">
            <th className="py-1.5 pr-2" />
            <th className="py-1.5 pr-2">Marker</th>
            <th className="py-1.5 pr-2">Today (everyone)</th>
            <th className="py-1.5 pr-2">Boys / Men</th>
            <th className="py-1.5 pr-2">Girls / Women</th>
            <th className="py-1.5 pr-2" title="Applies to athletes aged 14 or younger on the lab date, boys and girls">Under 15 (all)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.key} className="border-t border-[#1E304B]" data-testid={`gender-preset-row-${p.key}`}>
              <td className="py-2 pr-2">
                <input type="checkbox" checked={!!selected[p.key]} onChange={(e) => setSelected(p.key, e.target.checked)}
                       data-testid={`gender-preset-select-${p.key}`} />
              </td>
              <td className="py-2 pr-2 text-slate-100">{p.label} <span className="text-slate-500">({p.unit})</span></td>
              <td className="py-2 pr-2 text-slate-400 font-mono-data" data-testid={`gender-preset-current-${p.key}`}>
                {fmtBand(p.current_default)}
                {(p.current_male || p.current_female) && (
                  <div className="text-[10px] text-cyan-300">M {fmtBand(p.current_male)} · F {fmtBand(p.current_female)}</div>
                )}
                {p.current_under15 && (
                  <div className="text-[10px] text-violet-300">&lt;15 {fmtBand(p.current_under15)}</div>
                )}
              </td>
              {["male", "female"].map((side) => (
                <td key={side} className="py-2 pr-2">
                  <div className="flex items-center gap-1">
                    <BandInput value={p[side].low} onChange={(v) => setBand(p.key, side, "low", v)} testid={`gender-preset-${p.key}-${side}-low`} />
                    <span className="text-slate-500">–</span>
                    <BandInput value={p[side].high} onChange={(v) => setBand(p.key, side, "high", v)} testid={`gender-preset-${p.key}-${side}-high`} />
                  </div>
                </td>
              ))}
              <td className="py-2 pr-2">
                {p.under15 ? (
                  <div className="flex items-center gap-1">
                    <BandInput value={p.under15.low} onChange={(v) => setBand(p.key, "under15", "low", v)} testid={`gender-preset-${p.key}-under15-low`} />
                    <span className="text-slate-500">–</span>
                    <BandInput value={p.under15.high} onChange={(v) => setBand(p.key, "under15", "high", v)} testid={`gender-preset-${p.key}-under15-high`} />
                  </div>
                ) : <span className="text-slate-600">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ImpactPreview({ preview, rows }) {
  const label = Object.fromEntries(rows.map((r) => [r.key, r.label]));
  return (
    <div className="mt-5 space-y-3" data-testid="gender-presets-impact">
      <div className="text-[12px] text-slate-300" data-testid="gender-presets-impact-summary">
        <b className="text-slate-100">{preview.total_flips}</b> athlete flag{preview.total_flips === 1 ? "" : "s"} would change
        (based on each athlete's latest lab).
        {preview.athletes_without_gender > 0 && (
          <span className="text-amber-300"> {preview.athletes_without_gender} tested athlete(s) have no gender on file and keep the shared range.</span>
        )}
      </div>
      {preview.markers.map((m) => (
        <div key={m.key} className="rounded border border-[#1E304B] bg-[#0B1627] p-3" data-testid={`gender-presets-impact-${m.key}`}>
          <div className="text-[12px] text-slate-200">
            {label[m.key] || m.key}: {m.tested} tested · <span className="text-amber-300">{m.became_flagged} newly flagged</span> ·{" "}
            <span className="text-emerald-300">{m.became_ok} cleared</span>
          </div>
          {m.flips.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
              {m.flips.map((f) => (
                <span key={f.sailor_id} className="text-slate-400">
                  {f.name} ({f.gender || "?"}{f.age != null ? `, ${f.age}y` : ""}) {f.value}: <span className={STATUS_CLS[f.before]}>{f.before}</span> → <span className={STATUS_CLS[f.after]}>{f.after}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AppliedBanner({ done, onClose }) {
  return (
    <div className="rounded border border-emerald-500/40 bg-emerald-500/10 p-4 text-emerald-200 text-sm" data-testid="gender-presets-applied">
      Applied male/female ranges for {done.count} marker{done.count === 1 ? "" : "s"} ({done.applied.join(", ")}).
      Flags, Urgent list, Advisory and reports now use each athlete's own band.
      <div className="mt-3"><button onClick={onClose} className="pill active" data-testid="gender-presets-done-btn">Done</button></div>
    </div>
  );
}
